import { Component, OnInit } from '@angular/core';
import { Router, Routes } from '@angular/router';
import { SessionContext } from '../../../core/services/config/app.settings';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { RefCampagnesService } from '../../../data/declaration/services/ref-campagnes/ref-campagnes.service';
import { TypeDr } from '../../../data/declaration/models/enums/type-dr.enum';
import { switchMap, tap } from 'rxjs/operators';
import { DeclarationsService } from '../../../data/declaration/services/declarations/declarations.service';
import { SuiviDrDrev } from '../../../data/declaration/models/suivi-dr-drev.model';
import * as moment from 'moment/moment';
import { LoaderService } from '../../../core/services/loader/loader.service';
import { SharedModule } from '../../../shared/shared.module';

@Component({
    selector: 'app-dr-drev-saisie',
    templateUrl: './dr-drev-saisie.component.html',
    styleUrls: ['./dr-drev-saisie.component.scss'],
    standalone: true,
    imports: [SharedModule]
})
export class DrDrevSaisieComponent implements OnInit {
    /**
     * Routes
     */
    static routes: Routes = [
        {
            path: '',
            redirectTo: 'liste',
            pathMatch: 'full',
            data: {
                role: 'ORGANISME && DECLARATION_MOUVEMENT_LECTURE',
                menu: { icon: '', libelle: 'menu.dr-drev-saisies', hide: true }
            }
        },
        {
            path: 'liste',
            component: DrDrevSaisieComponent
        }
    ];

    /**
     * Get idOrganisme from session context
     */
    get idOrganisme() {
        return SessionContext.get('idOrganisme');
    }

    /**
     * Search forme
     */
    public searchForm: FormGroup;

    /**
     * Getter annee
     */
    get annee() {
        return this.searchForm.get('annee');
    }

    /**
     * Getter widthDR
     */
    get withDR() {
        return this.searchForm.get('withDR');
    }

    /**
     * Getter typeDR
     */
    get typeDR() {
        return this.searchForm.get('typeDR');
    }

    /**
     * Getter widthDREV
     */
    get withDREV() {
        return this.searchForm.get('withDREV');
    }

    /**
     * Getter raisonSociale
     */
    get raisonSociale() {
        return this.searchForm.get('raisonSociale');
    }

    /**
     * Getter dateDebutDepotDR
     */
    get dateDebutDepotDR() {
        return this.searchForm.get('dateDebutDepotDR');
    }

    /**
     * Getter dateFinDepotDR
     */
    get dateFinDepotDR() {
        return this.searchForm.get('dateFinDepotDR');
    }

    /**
     * Getter dateDebutSaisieDREV
     */
    get dateDebutSaisieDREV() {
        return this.searchForm.get('dateDebutSaisieDREV');
    }

    /**
     * Getter dateFinSaisieDREV
     */
    get dateFinSaisieDREV() {
        return this.searchForm.get('dateFinSaisieDREV');
    }

    /**
     * Enum TypeDr
     */
    readonly TypeDr = TypeDr;

    /**
     * Dynamique table columns
     */
    columns = [
        { field: 'raisonSociale', label: 'Raison sociale' },
        { field: 'cvi', label: 'CVI' },
        { field: 'dateDepotDR', label: 'Date dépôt DR' },
        { field: 'typeDR', label: 'Type DR' },
        { field: 'superficie', label: 'Superficie DR (ha)' },
        { field: 'volRevendicableDR', label: 'Vol. revendicable DR (hl)' },
        { field: 'dateSaisieDREV', label: 'Date saisie DREV' },
        { field: 'volTotalRevendique', label: 'Vol. total revendiqué (hl)' }
    ];

    /**
     * List of suiviDrDrev
     */
    suiviDrDrev: SuiviDrDrev[] = [];

    /**
     * Params from home page
     */
    state: any;

    constructor(
        private readonly router: Router,
        private readonly fb: FormBuilder,
        private readonly refCampagnesService: RefCampagnesService,
        private readonly declarationsService: DeclarationsService,
        private readonly loaderService: LoaderService
    ) {
        this.state = this.router.getCurrentNavigation() && this.router.getCurrentNavigation().extras.state;
    }

    /**
     * On init component
     */
    ngOnInit() {
        // Instancier le formulaire de recherche
        this.searchForm = this.fb.group({
            annee: [undefined, Validators.required],
            withDR: undefined,
            typeDR: undefined,
            withDREV: undefined,
            raisonSociale: undefined,
            dateDebutDepotDR: undefined,
            dateFinDepotDR: undefined,
            dateDebutSaisieDREV: undefined,
            dateFinSaisieDREV: undefined
        });

        this.initSuiviDrDrev();
    }

    /**
     * Get params from the home page
     */
    initSuiviDrDrev() {
        if (this.state) {
            this.searchForm.patchValue({
                annee: this.state.annee,
                withDR: this.state.withDR,
                withDREV: this.state.withDREV,
                typeDR: this.state.typeDR === TypeDr.TOUTES ? null : this.state.typeDR
            });
            this.search();
        } else {
            this.refCampagnesService
                .getCampagneEnCours()
                .pipe(
                    tap(campagne => this.annee.setValue(campagne.annee.toString())),
                    switchMap(campagne =>
                        this.declarationsService.getSuiviDrDrev(
                            this.idOrganisme,
                            campagne.annee.toString(),
                            this.raisonSociale.value,
                            this.withDR.value,
                            this.typeDR.value,
                            this.withDREV.value,
                            this.getMomentDate(this.dateDebutDepotDR.value),
                            this.getMomentDate(this.dateFinDepotDR.value),
                            this.getMomentDate(this.dateDebutSaisieDREV.value),
                            this.getMomentDate(this.dateFinSaisieDREV.value)
                        )
                    ),
                    this.loaderService.operator()
                )
                .subscribe(suiviDrDrev => {
                    this.suiviDrDrev = suiviDrDrev;
                });
        }
    }

    /**
     * Filter the search
     */
    search() {
        this.declarationsService
            .getSuiviDrDrev(
                this.idOrganisme,
                this.annee.value,
                this.raisonSociale.value,
                this.withDR.value,
                this.typeDR.value,
                this.withDREV.value,
                this.getMomentDate(this.dateDebutDepotDR.value),
                this.getMomentDate(this.dateFinDepotDR.value),
                this.getMomentDate(this.dateDebutSaisieDREV.value),
                this.getMomentDate(this.dateFinSaisieDREV.value)
            )
            .pipe(this.loaderService.operator())
            .subscribe(suiviDrDrev => {
                this.suiviDrDrev = suiviDrDrev;
            });
    }

    /**
     * Go to the dashboard of the organisme
     */
    goToDashboard(operateurId: number) {
        this.router.navigate(['/main/operateurs/or', operateurId, 'dashboard']);
    }

    /**
     * Get moment date
     * @param date - Selected date
     */
    private getMomentDate(date: { year: number; month: number; day: number }): moment.Moment {
        return date ? moment([date.year, date.month - 1, date.day, 0, 0, 0]) : null;
    }
}
